import { Component, OnInit, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-prescription-lang-dialog',
    template: `
    <div class="lang-dialog" dir="rtl">
      <h3>בחר שפה להדפסה</h3>
      <div class="lang-btns">
        <button class="btn-lang" (click)="select('he')">עברית</button>
        <button class="btn-lang" (click)="select('en')">English</button>
      </div>
      <div class="lang-cancel">
        <button class="btn-cancel" (click)="cancel()">ביטול</button>
      </div>
    </div>
  `,
    styles: [`
    .lang-dialog { padding: 24px 32px; min-width: 260px; text-align: center; }
    h3 { margin: 0 0 20px; font-size: 1.1em; color: #022931; }
    .lang-btns { display: flex; gap: 12px; justify-content: center; margin-bottom: 16px; }
    .btn-lang { padding: 10px 28px; border: 1.5px solid #022931; background: #fff; color: #022931; border-radius: 6px; font-size: 1em; cursor: pointer; font-family: inherit; transition: background 0.15s; }
    .btn-lang:hover { background: #ebf1f1; }
    .btn-cancel { background: none; border: none; color: #888; cursor: pointer; font-size: 0.9em; text-decoration: underline; }
  `],
    standalone: false
})
export class PrescriptionLangDialogComponent {
  constructor(private ref: MatDialogRef<PrescriptionLangDialogComponent>) {}
  select(lang: string) { this.ref.close(lang); }
  cancel() { this.ref.close(null); }
}
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PrescriptionService, Drug, PrescriptionItem } from '../prescription.service';
import { LoginService } from '../login.service';
import { UNITS, TIMINGS, ROUTES, INTERVAL_UNITS, FREQUENCIES, FREQUENCY_OTHER, LookupItem } from '../prescription/prescription-lookups';

export interface PrescriptionDialogData {
  PID: string;
  GID: number;
  patientName: string;
  patientID: string;
  patientDOB: string;
  HMO:string,
  examID?: number;
  existingPrescriptionId?: number;
  existingCreatedAt?: string;
}

@Component({
    selector: 'app-prescription-dialog',
    templateUrl: './prescription-dialog.component.html',
    styleUrls: ['./prescription-dialog.component.scss'],
    standalone: false
})
export class PrescriptionDialogComponent implements OnInit {

  drugSearchCtrl = new FormControl('');
  dosageCtrl = new FormControl('');
  unitCtrl = new FormControl('');
  routeCtrl = new FormControl('');
  timingCtrl = new FormControl('');
  frequencyCtrl = new FormControl('');
  frequencyCustomCtrl = new FormControl('');
  intervalValueCtrl = new FormControl<number>(null);
  intervalUnitCtrl = new FormControl('day');
  instructionsCtrl = new FormControl('');

  // Lookup lists for the dropdowns
  units: LookupItem[] = UNITS;
  routes: LookupItem[] = ROUTES;
  timings: LookupItem[] = TIMINGS;
  frequencies: LookupItem[] = FREQUENCIES;
  frequencyOther = FREQUENCY_OTHER;
  intervalUnits: LookupItem[] = INTERVAL_UNITS;

  filteredDrugs: Drug[] = [];
  selectedDrug: Drug = null;
  items: (PrescriptionItem & { _tempId?: number })[] = [];
  displayColumns = ['drugName', 'dosage', 'unit', 'route', 'timing', 'frequency', 'intervalValue', 'intervalUnit', 'instructions', 'remove'];
  prescriptionId: number = null;
  status: string = 'draft';
  signedAt: string = null;
  createdAt: string = null;
  loading = false;
  saving = false;
  signing = false;
  errorMsg = '';
  signerName = '';
  signerTitle = '';
  private _tempCounter = 0;

  // Narcotic prescription (header-level)
  isNarcotic = false;
  doctorIdCtrl = new FormControl('');
  narcoticInstructionsCtrl = new FormControl('');
  doctorPersonalId = '';
  doctorIdOnFile = false;

  private hmoMap: { [key: string]: string } = {
    'Clalit':   'כללית',
    'Macabi':   'מכבי',
    'Meuhedet': 'מאוחדת',
    'Leumit':   'לאומית',
    'Private':  'פרטי',
    'IDF':      'צבאי',
  };

  getHMO(hmo: string): string {
    return this.hmoMap[hmo] || hmo;
  }


  constructor(
    public dialogRef: MatDialogRef<PrescriptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PrescriptionDialogData,
    private prescriptionService: PrescriptionService,
    public ls: LoginService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
     this.createdAt =null
    if (this.data.existingCreatedAt) {
      this.createdAt = this.data.existingCreatedAt;
    }
    if (this.data.existingPrescriptionId) {
      // Load existing prescription
      this.prescriptionId = this.data.existingPrescriptionId;
      this.loading = true;
      this.prescriptionService.getOne(this.data.existingPrescriptionId).subscribe(
        res => {
          this.status = res.Status || 'draft';
          this.signedAt = res.SignedAt;
          this.createdAt = res.CreatedAt || this.createdAt;
          this.signerName = res.Opener || '';
          this.signerTitle = res.OpenerTitle || '';
          this.isNarcotic = !!res.IsNarcotic;
          this.doctorIdCtrl.setValue(res.DoctorID || '');
          this.narcoticInstructionsCtrl.setValue(res.NarcoticInstructions || '');
          this.items = (res.items || []).map(i => ({ ...i, _tempId: ++this._tempCounter }));
          this.loading = false;
        },
        err => {
          this.errorMsg = 'Failed to load prescription';
          this.loading = false;
        }
      );
    } else {
      // Create the prescription record immediately (draft)
      this.loading = true;
      this.prescriptionService.create(this.data.PID, this.data.GID, this.data.examID).subscribe(
        res => {
          this.prescriptionId = res.AutoIndex;
          this.createdAt = new Date().toISOString();
          this.loading = false;
        },
        err => {
          this.errorMsg = 'Failed to create prescription';
          this.loading = false;
        }
      );
    }

    // Prefill the narcotic doctor-ID field from the doctor's profile (already loaded at login), if we have it on file
    this.doctorPersonalId = this.ls.personalId || '';
    this.doctorIdOnFile = !!this.doctorPersonalId;
    this.applyDoctorIdDefault();

    // Drug search autocomplete
    this.drugSearchCtrl.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(val => {
        if (typeof val === 'string' && val.length >= 2) {
          return this.prescriptionService.searchDrugs(val);
        }
        return of([]);
      })
    ).subscribe(drugs => {
      this.filteredDrugs = drugs;
      this.selectedDrug = null;
    });
  }

  private resetItemForm() {
    this.dosageCtrl.reset('');
    this.unitCtrl.reset('');
    this.routeCtrl.reset('');
    this.timingCtrl.reset('');
    this.frequencyCtrl.reset('');
    this.frequencyCustomCtrl.reset('');
    this.intervalValueCtrl.reset(null);
    this.intervalUnitCtrl.reset('day');
    this.instructionsCtrl.reset('');
  }

  selectDrug(drug: Drug) {
    this.selectedDrug = drug;
    this.drugSearchCtrl.setValue(drug.name, { emitEvent: false });
    this.filteredDrugs = [];
    this.resetItemForm();
  }

  // Manual entry: use the typed text as a free-text drug when none matched the search.
  addManualDrug() {
    const typed = (this.drugSearchCtrl.value || '').toString().trim();
    if (!typed) return;
    this.selectedDrug = { code: '', name: typed, nameEn: '', activeIngredient: '' };
    this.filteredDrugs = [];
  }

  addItem() {
    if (!this.selectedDrug || !this.prescriptionId) return;
    const item: PrescriptionItem = {
      DrugName: this.selectedDrug.name,
      DrugCode: this.selectedDrug.code,
      Dosage: this.dosageCtrl.value,
      Unit: this.unitCtrl.value || '',
      Route: this.routeCtrl.value || '',
      TimingCode: this.timingCtrl.value || '',
      FrequencyCode: this.frequencyCtrl.value === this.frequencyOther ? null : (this.frequencyCtrl.value || null),
      FrequencyCustom: this.frequencyCtrl.value === this.frequencyOther ? (this.frequencyCustomCtrl.value || '').toString().trim() : '',
      IntervalValue: this.intervalValueCtrl.value || null,
      IntervalUnit: this.intervalValueCtrl.value ? (this.intervalUnitCtrl.value || 'day') : null,
      Instructions: this.instructionsCtrl.value || ''
    };
    this.saving = true;
    this.prescriptionService.addItem(this.prescriptionId, item).subscribe(
      res => {
        this.items = [...this.items, { ...item, AutoIndex: res.AutoIndex, _tempId: ++this._tempCounter }];
        this.selectedDrug = null;
        this.drugSearchCtrl.reset('');
        this.resetItemForm();
        this.saving = false;
      },
      err => {
        this.errorMsg = 'Failed to add item';
        this.saving = false;
      }
    );
  }

  // Display helpers for the items table (translate stored codes to Hebrew labels)
  unitLabel(code: string): string { return this.labelOf(this.units, code); }
  routeLabel(code: string): string { return this.labelOf(this.routes, code); }
  timingLabel(code: string): string { return this.labelOf(this.timings, code); }
  intervalUnitLabel(code: string): string { return this.labelOf(this.intervalUnits, code); }
  frequencyLabel(item: PrescriptionItem): string {
    if (item.FrequencyCustom) return item.FrequencyCustom;
    return this.labelOf(this.frequencies, item.FrequencyCode);
  }
  private labelOf(list: LookupItem[], code: string): string {
    if (!code) return '';
    const found = list.find(x => x.code === code);
    return found ? found.he : code;
  }

  removeItem(item: PrescriptionItem) {
    if (!item.AutoIndex || !this.prescriptionId) return;
    this.prescriptionService.removeItem(this.prescriptionId, item.AutoIndex).subscribe(
      () => {
        this.items = this.items.filter(i => i.AutoIndex !== item.AutoIndex);
      },
      err => {
        this.errorMsg = 'Failed to remove item';
      }
    );
  }

  get narcoticValid(): boolean {
    if (!this.isNarcotic) return true;
    return !!(this.doctorIdCtrl.value || '').toString().trim()
        && !!(this.narcoticInstructionsCtrl.value || '').toString().trim();
  }

  toggleNarcotic() {
    if (this.status === 'signed' || this.status === 'printed') return;
    this.isNarcotic = !this.isNarcotic;
    if (this.isNarcotic) {
      this.applyDoctorIdDefault();
    }
  }

  // Fill the doctor-ID field from the profile only if it's not already set
  // (e.g. from a previously saved narcotic prescription).
  private applyDoctorIdDefault() {
    if (!this.doctorIdCtrl.value && this.doctorPersonalId) {
      this.doctorIdCtrl.setValue(this.doctorPersonalId);
    }
  }

  sign() {
    if (!this.prescriptionId || this.items.length === 0) return;
    if (!this.narcoticValid) {
      this.errorMsg = 'במרשם נרקוטי יש למלא ת"ז רופא והוראות';
      return;
    }
    this.errorMsg = '';
    this.signing = true;
    // Persist narcotic fields before signing so the printout reflects them.
    this.prescriptionService.saveNarcotic(this.prescriptionId, {
      IsNarcotic: this.isNarcotic,
      DoctorID: (this.doctorIdCtrl.value || '').toString().trim(),
      NarcoticInstructions: (this.narcoticInstructionsCtrl.value || '').toString().trim()
    }).subscribe(
      () => this.doSign(),
      () => this.doSign()
    );
  }

  private doSign() {
    this.prescriptionService.sign(this.prescriptionId).subscribe(
      (res: any) => {
        this.status = 'signed';
        this.signedAt = new Date().toISOString();
        this.signerName = this.ls.username;
        this.signerTitle = (res && res.title) ? res.title : '';
        this.signing = false;
      },
      err => {
        this.errorMsg = 'Failed to sign prescription';
        this.signing = false;
      }
    );
  }

  openPrint() {
    const ref = this.dialog.open(PrescriptionLangDialogComponent, { width: '320px', panelClass: 'rx-lang-dialog' });
    ref.afterClosed().subscribe(lang => {
      if (lang) { this.print(lang); }
    });
  }

  print(lang: string) {
    this.prescriptionService.updateStatus(this.prescriptionId, 'printed').subscribe();
    window.open(`/api/prescriptions/printpdf/${this.prescriptionId}/${lang}`, '_blank');
  }

  close() {
    this.dialogRef.close({ prescriptionId: this.prescriptionId, status: this.status });
  }
}
